import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { DURATION_TIME_UNITS_ALL, MOMENT_DURATION_UNITS } from '@/main/app.constants';
import { SearchTypeEnum as _SearchTypeEnum } from '@seeqdev/api/model/PropertySearchV1';
import type { ReactSelectOption } from '@/core/IconSelect.molecule';
import type { HierarchyPath } from '@/itemFinder/itemFinder.store';
import type { MomentDurationUnit } from '@/main/app.constants';
import type { SearchPropertyOption } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { FixedListSearchV1 } from '@seeqdev/api/model/FixedListSearchV1';
import type { HierarchySearchV1 } from '@seeqdev/api/model/HierarchySearchV1';
import type { ItemFinderSearchesInputV1 } from '@seeqdev/api/model/ItemFinderSearchesInputV1';
import type { OperatorEnum } from '@seeqdev/api/model/PropertyFilterInputV1';
import type { PropertySearchV1 } from '@seeqdev/api/model/PropertySearchV1';
import type { ScalingTableUUIDColumnSearchV1 } from '@seeqdev/api/model/ScalingTableUUIDColumnSearchV1';
import type { StoredTableSearchV1 } from '@seeqdev/api/model/StoredTableSearchV1';

/**
 * Shared ItemFinder model only.
 *
 * Do not add consumer-specific pseudo-search types, UI-only fields, constants, or helper types here.
 * Examples that do not belong here include Vantage-only workbook/folder/tree modes, Scaling Tables-only
 * column-selection state, Tree Builder-only descendant-selection state, consumer-specific page sizes,
 * and consumer-specific option labels.
 *
 * Put those in the owning consumer module and adapt them to/from the shared persisted ItemFinder search model
 * at the consumer boundary.
 */

export type Value = {
  value: string | number;
  propertyId: string;
  searchId: string | number;
  valueId: string | undefined;
};

export type Property = {
  propertyId: string;
  propertyName: string;
  operator: OperatorEnum;
  datasources?: Array<{
    label: string;
    value: { name: string; id: string; datasourceId: string; datasourceClass: string };
  }>;
  values: Value[];
  searchId: string;
  columnId?: string;
  columns?: Array<{
    label: string;
    value: string;
  }>;
  isColumnSelect?: boolean;
};

export type AncestorPredicate = {
  ancestorPredicateId: string;
  predicates: Property[];
  level?: number;
};

interface SearchExtraProps {
  searchId: string | number;
  ancestorId?: string;
  folderId?: string;
  predicates?: Property[];
  types?: string[];
  isInclude?: boolean;
  storedTableColumns?: ReactSelectOption[];
  searchOrder?: number;
  isSimpleSearch?: boolean;
  ancestorPredicates?: AncestorPredicate[];
  hierarchyTableProperties?: SearchPropertyOption[];
  hierarchyLevelColumns?: HierarchyLevelColumn[];
  hierarchySearchMode?: HierarchySearchMode;
  hierarchyPaths?: HierarchyPath[];
  searchItemTable?: SelectedSearchItem;
  searchHierarchy?: SelectedSearchItem;
  scalingTable?: SelectedSearchItem;
  scalingColumn?: SelectedSearchItem;
  scalingTableColumns?: ColumnDefinition[];
  /** Case-insensitive substring used to filter the names shown in the checkbox list. */
  assetLeafChildFilter?: string;
  /** The names the user has checked. Ignored when `selectAllChildren` is true. */
  selectedChildNames?: string[];
  /** When true, all current and future leaf descendants of the asset are included (a dynamic descendant query). */
  selectAllChildren?: boolean;
  /**
   * When true, `selectedChildNames` is known to already contain every name matching `assetLeafChildFilter` (set
   * after a filtered "select all" bulk action fully materializes them from the backend). Lets the "select all"
   * checkbox render checked immediately even when the UI's own paginated name list hasn't finished loading every
   * page, so a second click can toggle it back off instead of re-sending `true`. Reset to false whenever the
   * filter, asset, or types change, or an individual name is toggled.
   */
  allFilteredChildrenSelected?: boolean;
}

export type HierarchySearchOptions = {
  hierarchyTableProperties: SearchPropertyOption[];
  hierarchyLevelColumns: HierarchyLevelColumn[];
  hierarchy: SelectedSearchItem;
  itemTable: SelectedSearchItem;
};

export type PropertySearch = Omit<PropertySearchV1, 'predicates'> & SearchExtraProps;
export type FixedListSearch = FixedListSearchV1 & SearchExtraProps;
export type StoredTableSearch = Omit<StoredTableSearchV1, 'predicates'> & SearchExtraProps;
export type HierarchySearch = HierarchySearchV1 & SearchExtraProps;
export type ScalingTableSearch = ScalingTableUUIDColumnSearchV1 & SearchExtraProps;
export type OtherSearch = { searchType: string } & SearchExtraProps;

export type AIPayload = Omit<ItemFinderSearchesInputV1, 'limit'>;

export interface HierarchyLevelColumn {
  columnId: string;
  columnName: string;
  orderValue: number;
  columnType: string;
}

enum ExtraSearchTypes {
  FOLDER = 'PROPERTY_FOLDER',
  WORKBENCH = 'PROPERTY_WORKBENCH',
  ASSET_TREE = 'PROPERTY_ASSET_TREE',
  ASSET_LEAF_CHILDREN = 'PROPERTY_ASSET_LEAF_CHILDREN',
}

/**
 * A distinct child name under the selected asset and the number of items sharing it, along with the distinct item
 * types among those items. Usually a single type, but a name may be shared by items of different requested leaf
 * types (e.g. a Signal and a Condition with the same name).
 */
export type AssetLeafChildName = {
  name: string;
  count: number;
  types: string[];
};

/** Page size for the Tree Builder "asset leaf children" grouped-name list. */
export const ASSET_LEAF_CHILDREN_PAGE_SIZE = 40;

/** Max page size the backend allows (see PaginationParameters.MAX_PAGE_SIZE); used when fetching all names to
 *  materialize a "select all" search into individual names. */
export const MAX_BACKEND_PAGE_SIZE = 1000;

export const SearchTypeEnum = {
  ..._SearchTypeEnum,
  ...ExtraSearchTypes,
} as const;

export enum HierarchySearchMode {
  SIMPLE = 'SIMPLE',
  PROPERTY = 'PROPERTY',
  FIXED_LIST = 'FIXED_LIST',
}

export type SearchType = (typeof SearchTypeEnum)[keyof typeof SearchTypeEnum];

export type Search =
  | FixedListSearch
  | PropertySearch
  | OtherSearch
  | HierarchySearch
  | StoredTableSearch
  | ScalingTableSearch;

export const SearchOptions = [
  { value: SearchTypeEnum.PROPERTY, text: 'ITEM_FINDER.PROPERTY', description: 'ITEM_FINDER.PROPERTY_DESCRIPTION' },
  {
    value: SearchTypeEnum.FIXEDLIST,
    text: 'ITEM_FINDER.FIXED_LIST.SEARCH_OPTION',
    description: 'ITEM_FINDER.FIXED_LIST_DESCRIPTION',
  },
  {
    value: SearchTypeEnum.HIERARCHY,
    text: 'ITEM_FINDER.HIERARCHY_SEARCH',
    description: 'ITEM_FINDER.HIERARCHY_SEARCH_DESCRIPTION',
  },
  {
    value: SearchTypeEnum.STOREDTABLE,
    text: 'ITEM_FINDER.STORED_TABLE_SEARCH',
    description: 'ITEM_FINDER.STORED_TABLE_SEARCH_DESCRIPTION',
  },
];

export const searchTypesNonAssets = [
  SeeqNames.Types.CalculatedCondition,
  SeeqNames.Types.StoredCondition,
  SeeqNames.Types.Condition,
  SeeqNames.Types.CalculatedSignal,
  SeeqNames.Types.StoredSignal,
  SeeqNames.Types.CalculatedScalar,
  SeeqNames.Types.LiteralScalar,
  SeeqNames.Types.ThresholdMetric,
];

export const propertyTypes = [
  { value: SeeqNames.Types.Condition, text: 'ITEM_FINDER.TYPES_CONDITION' },
  { value: SeeqNames.Types.Signal, text: 'ITEM_FINDER.TYPES_SIGNAL' },
  { value: SeeqNames.Types.Scalar, text: 'ITEM_FINDER.TYPES_SCALAR' },
  { value: SeeqNames.Types.ThresholdMetric, text: 'ITEM_FINDER.TYPES_THRESHOLD_METRIC' },
  { value: SeeqNames.Types.Asset, text: 'ITEM_FINDER.TYPES_ASSET' },
];

/** Default item types when resetting a stored-table simple (name column) search. */
export const DEFAULT_STORED_TABLE_SIMPLE_SEARCH_TYPES = [
  SeeqNames.Types.Asset,
  SeeqNames.Types.Signal,
  SeeqNames.Types.Condition,
  SeeqNames.Types.Scalar,
];

export const defaultLookBack: ValueWithUnitsItem = {
  units: MOMENT_DURATION_UNITS.DAYS,
  value: 7,
};

export const defaultLookAhead: ValueWithUnitsItem = {
  units: MOMENT_DURATION_UNITS.DAYS,
  value: 0,
};

/** 30 days */
export const MAX_SAFE_LOOK_BACK_IN_SECONDS = 30 * 24 * 60 * 60;

const DURATION_UNITS_TO_INCLUDE: MomentDurationUnit[] = ['h', 'd', 'w'];

export const DURATION_UNITS = DURATION_TIME_UNITS_ALL.filter(
  (unit) => unit.momentUnit && DURATION_UNITS_TO_INCLUDE.includes(unit.momentUnit as MomentDurationUnit),
);

export const ITEM_FINDER_SEARCH_DRAG_TYPE = 'ITEM_FINDER_SEARCH';

export const HIERARCHY_SUBTITLES = new Map<HierarchySearchMode, string>([
  [HierarchySearchMode.SIMPLE, 'ITEM_FINDER.HIERARCHY_SUBTITLE_SIMPLE'],
  [HierarchySearchMode.PROPERTY, 'ITEM_FINDER.HIERARCHY_SUBTITLE_PROPERTY'],
  [HierarchySearchMode.FIXED_LIST, 'ITEM_FINDER.HIERARCHY_SUBTITLE_FIXED_LIST'],
]);
